import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';

import { PASS_ORIGIN } from '@/lib/api/pass-origin';
import useIsPWA from '@/hooks/use-is-pwa';
import { setSession, type Session } from '@/lib/storage';
import { useUiStore } from '@/stores/ui-store';
import { isMobile } from '@/lib/helpers';

import AccessibilityPanel from '@/components/shared/accessibility/accessibility-panel';
import AccimotoPage from '@/pages/accimoto/accimoto-page';
import Login from '@/pages/auth/login-page';
import Maintenance from '@/pages/maintenance/maintenance-page';
import PrivateRoute from '@/components/auth/private-route';
import Whitelabel from '@/components/auth/whitelabel-gate';

interface OttExchangeResponse {
    message_footer?: string | null;
    message_warning?: string | null;
    img_footer?: string | null;
    user?: { whitelabel?: number | string };
    gravitee_logout?: Session['gravitee_logout'];
}

/**
 * Bascule en maintenance quand le Pass répond 503 (cf. `lib/api/client`).
 *
 * Reprend l'ancienne route `*` conditionnelle : la connexion et `/indisponible`, plus
 * spécifiques, restaient accessibles ; tout le reste renvoie vers `/maintenance`.
 */
const MaintenanceGuard = () => {
    const maintenance = useUiStore((state) => state.maintenance);
    const { pathname } = useLocation();
    const exempt = ['/maintenance', '/indisponible', '/login'].some((prefix) => pathname.startsWith(prefix));

    return maintenance && !exempt ? <Navigate to="/maintenance" replace /> : <Outlet />;
};

const router = createBrowserRouter([
    {
        element: <MaintenanceGuard />,
        children: [
            { path: '/maintenance', element: <Maintenance /> },
            { path: '/indisponible', element: <p>indisponible</p> },
            { path: '/login/*', element: <Login /> },
            // Liens signés des mails Accimoto : publics, le client n'a pas à se connecter.
            { path: '/accimoto/:accidentId/*', element: <AccimotoPage /> },
            { path: '/*', element: <PrivateRoute component={Whitelabel} /> },
        ],
    },
]);

/**
 * Racine : auto-connexions par URL (OTT, jeton des iframes), puis routage entre connexion,
 * maintenance et espace privé.
 */
const App = () => {
    const isPWA = useIsPWA();

    // Gestion de l'auto-login via OTT (One-Time Token) pour redirection cross-domain
    // L'OTT est passé en fragment (#ott=) plutôt qu'en query string (?ott=) :
    // le fragment n'est pas transmis au serveur (logs nginx/CDN) ni au Referer.
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const ottFromUrl = hashParams.get('ott');

    if (ottFromUrl) {
        // Nettoyer l'URL immédiatement pour ne pas exposer l'OTT dans l'historique
        window.history.replaceState(
            {},
            '',
            window.location.pathname + window.location.search
        );

        const passBaseUrl = PASS_ORIGIN;

        // Purge des cookies Gravitee d'une éventuelle session précédente avant
        // l'OTT exchange. Sans ça, un user déjà authentifié via Gravitee garde
        // son gravitee_access_token qui prime sur le nouveau auth_token dans
        // CustomAuthMiddleware (priorité Bearer > gravitee > auth_token) →
        // l'utilisateur reste authentifié comme l'ancien compte au lieu du nouveau.
        // Le .finally() garantit qu'on tente l'OTT exchange même si la purge échoue.
        const exchangeOtt = () => fetch(`${passBaseUrl}/auth/ott/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ott: ottFromUrl }),
        })
            .then(res => {
                if (!res.ok) throw new Error('OTT invalide ou expiré');
                return res.json() as Promise<OttExchangeResponse>;
            })
            .then(data => {
                const storage: Session = {
                    authenticated: true,
                    welcome: {
                        message_footer: data.message_footer,
                        message_warning: data.message_warning,
                        img_footer: data.img_footer,
                    },
                    id_whitelabel: data.user?.whitelabel,
                };
                // Cross-brand : contexte de logout du tenant Gravitee source.
                // Stocké pour qu'au logout on puisse invalider la session SSO
                // Gravitee qui a déclenché le redirect cross-brand (sinon le
                // SSO ré-authentifie silencieusement au prochain login).
                if (data.gravitee_logout) {
                    storage.gravitee_logout = data.gravitee_logout;
                }
                setSession(storage);
                window.location.reload();
            })
            .catch(() => {
                window.location.href = '/login';
            });

        fetch(`${passBaseUrl}/api/v2/auth/gravitee/logout`, {
            method: 'POST',
            credentials: 'include',
        })
            .catch(() => { /* silencieux — on tente l'OTT exchange dans tous les cas */ })
            .finally(exchangeOtt);

        return null;
    }

    // Gestion de l'auto-login via token passé en query param
    // IMPORTANT: Ceci doit s'exécuter AVANT le rendu pour éviter les redirections
    // L'auto-login ne s'applique qu'aux URLs commençant par /frames
    const tokenFromUrl = searchParams.get('token');
    const isFrameUrl = window.location.pathname.startsWith('/frames');

    if (tokenFromUrl && isFrameUrl) {
        // Stocker le token dans localStorage avec le format Bearer attendu
        setSession({ token: `Bearer ${tokenFromUrl}` });

        // Nettoyer l'URL pour ne pas exposer le token dans l'historique
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url);

        // Recharger pour prendre en compte le nouveau token
        window.location.reload();

        // Retourner null pendant le rechargement
        return null;
    }

    if (window.location.pathname.startsWith('/nouveau-mot-de-passe')) {
        window.location.href = `/login${window.location.pathname}`;
        return null;
    }

    return (
        <>
            <RouterProvider router={router} />
            {!isPWA && !isMobile() && <AccessibilityPanel />}
        </>
    );
};

export default App;
